// _public_movil/LiveResultsMovil.jsx
import React, { useMemo, useState, useEffect } from 'react';
import { FaTimes, FaSearch, FaClock } from 'react-icons/fa';
import { useLiveResults } from '../_shared/hooks/useLiveResults';
import './LiveResultsMovil.css';

const formatearFecha = (fecha) => {
  if (!fecha) return 'Sin información';
  const fechaValida = new Date(fecha);
  if (Number.isNaN(fechaValida.getTime())) return 'Fecha no válida';
  return fechaValida.toLocaleString('es-BO', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'America/La_Paz'
  });
};

const obtenerOpciones = (resultados, campo) => {
  return [
    ...new Set(
      resultados
        .map((r) => r?.[campo])
        .filter((v) => v !== null && v !== undefined && String(v).trim() !== '')
        .map((v) => String(v))
    )
  ].sort((a, b) =>
    a.localeCompare(b, 'es', { numeric: true, sensitivity: 'base' })
  );
};

const compararNumeros = (a, b) => {
  const parsear = (val) => {
    if (val === null || val === undefined) return Infinity;
    const str = String(val).trim();
    if (str === '') return Infinity;
    const num = Number(str);
    return Number.isFinite(num) ? num : Infinity;
  };
  const na = parsear(a);
  const nb = parsear(b);
  if (na === Infinity && nb === Infinity) return 0;
  return na - nb;
};

const compararDistanciasDesc = (a, b) => {
  const extraerKm = (val) => {
    if (val === null || val === undefined) return -Infinity;
    const match = String(val).match(/(\d+(?:\.\d+)?)/);
    return match ? Number(match[1]) : -Infinity;
  };
  return extraerKm(b) - extraerKm(a);
};

const esEstadoNoOk = (estado) => {
  if (!estado) return false;
  const e = String(estado).trim().toUpperCase();
  return e !== '' && e !== 'OK';
};

const LiveResultsMovil = ({ slug, onClose }) => {
  const {
    // Categorías
    resultados,
    disponible,
    cantidad,
    actualizadoEn,

    // General
    resultadosGeneral,
    disponibleGeneral,
    cantidadGeneral,
    generalActualizadoEn,

    // Compartido
    cargando,
    error,
    conexion
  } = useLiveResults(slug);

  const [busqueda, setBusqueda] = useState('');
  const [distancia, setDistancia] = useState('');
  const [viewMode, setViewMode] = useState('general');
  // 🆕 Género por defecto en General. Arranca con el primero disponible.
  const [genero, setGenero] = useState('');

  // 🔒 Bloqueo de scroll del body
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    const originalPosition = document.body.style.position;
    const originalTop = document.body.style.top;
    const scrollY = window.scrollY;

    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.top = originalTop;
      window.scrollTo(0, scrollY);
    };
  }, []);

  // 🆕 Auto-seleccionar el primer género disponible cuando entra a General
  useEffect(() => {
    if (viewMode === 'general' && !genero) {
      const generosBase = obtenerOpciones(resultadosGeneral, 'genero');
      if (generosBase.length > 0) {
        setGenero(generosBase[0]);
      }
    }
  }, [viewMode, genero, resultadosGeneral]);

  // Base según modo
  const resultadosBase = viewMode === 'general' ? resultadosGeneral : resultados;
  const disponibleActual =
    viewMode === 'general' ? disponibleGeneral : disponible;
  const cantidadActual =
    viewMode === 'general' ? cantidadGeneral : cantidad;
  const actualizadoEnActual =
    viewMode === 'general' ? generalActualizadoEn : actualizadoEn;

  const distancias = useMemo(
    () => obtenerOpciones(resultadosBase, 'distancia'),
    [resultadosBase]
  );

  // 🆕 Géneros disponibles en General (para el toggle)
  const generosDisponibles = useMemo(
    () => obtenerOpciones(resultadosGeneral, 'genero'),
    [resultadosGeneral]
  );

  // 🆕 Cambio de modo
  const handleViewModeChange = (nuevoModo) => {
    setViewMode(nuevoModo);
    if (nuevoModo === 'categorias') {
      setGenero('');
    }
  };

  const resultadosFiltrados = useMemo(() => {
    const termino = busqueda.trim().toLowerCase();

    return resultadosBase.filter((r) => {
      const coincideDistancia =
        !distancia || String(r.distancia ?? '') === distancia;

      // En General filtramos por género obligatorio.
      // En Categorías no aplica (la categoría ya incluye el género).
      const coincideGenero =
        viewMode === 'general'
          ? String(r.genero ?? '') === genero
          : true;

      const coincideBusqueda =
        !termino ||
        String(r.dorsal ?? '').toLowerCase().includes(termino) ||
        String(r.nombre ?? '').toLowerCase().includes(termino);

      return coincideDistancia && coincideGenero && coincideBusqueda;
    });
  }, [resultadosBase, busqueda, distancia, genero, viewMode]);

  const grupos = useMemo(() => {
    const groupsMap = new Map();

    resultadosFiltrados.forEach((r) => {
      const key =
        viewMode === 'general'
          ? String(r.distancia ?? 'Sin distancia')
          : `${r.distancia ?? 'Sin distancia'}||${r.categoria ?? 'Sin categoría'}`;

      if (!groupsMap.has(key)) {
        groupsMap.set(key, {
          distancia: r.distancia ?? 'Sin distancia',
          categoria: r.categoria ?? 'Sin categoría',
          resultados: []
        });
      }
      groupsMap.get(key).resultados.push(r);
    });

    const groupsArray = Array.from(groupsMap.values());

    groupsArray.sort((a, b) => {
      const cmpDist = compararDistanciasDesc(a.distancia, b.distancia);
      if (cmpDist !== 0) return cmpDist;
      if (viewMode === 'categorias') {
        return String(a.categoria).localeCompare(String(b.categoria), 'es', {
          sensitivity: 'base'
        });
      }
      return 0;
    });

    groupsArray.forEach((g) => {
      g.resultados.sort((a, b) => {
        const cmpPos = compararNumeros(a.posicion, b.posicion);
        if (cmpPos !== 0) return cmpPos;
        return compararNumeros(a.dorsal, b.dorsal);
      });
    });

    return groupsArray;
  }, [resultadosFiltrados, viewMode]);

  const totalVisibles = resultadosFiltrados.length;

  const estadoConexion = {
    conectado: { texto: 'En vivo', clase: 'is-connected' },
    reconectando: { texto: 'Reconectando...', clase: 'is-reconnecting' },
    desconectado: { texto: 'Desconectado', clase: 'is-disconnected' },
    conectando: { texto: 'Conectando...', clase: 'is-reconnecting' }
  };
  const estadoActual = estadoConexion[conexion] || estadoConexion.conectando;

  return (
    <div className="movil-live-results">
      <header className="movil-live-results__header">
        <div className="movil-live-results__header-left">
          <span className="movil-live-results__eyebrow">CRONOMETRAJE OFICIAL</span>
          <h1 className="movil-live-results__title">Resultados en Vivo</h1>
          <div className={`movil-live-results__conn ${estadoActual.clase}`}>
            <span className="movil-live-results__conn-dot" />
            {estadoActual.texto}
          </div>
        </div>

        <button
          type="button"
          className="movil-live-results__close"
          onClick={onClose}
          aria-label="Cerrar resultados"
        >
          <FaTimes />
        </button>
      </header>

      <div className="movil-live-results__body">
        {!cargando && disponibleActual && (
          <div className="movil-live-results__summary">
            <div className="movil-live-results__summary-item">
              <span>Recibidos</span>
              <strong>{cantidadActual}</strong>
            </div>
            <div className="movil-live-results__summary-item">
              <span>Visibles</span>
              <strong>{totalVisibles}</strong>
            </div>
            <div className="movil-live-results__summary-item movil-live-results__summary-item--full">
              <span>Actualizado</span>
              <strong>{formatearFecha(actualizadoEnActual)}</strong>
            </div>
          </div>
        )}

        {!cargando && disponibleActual && resultadosBase.length > 0 && (
          <div className="movil-live-results__filters">
            <div className="movil-live-results__search">
              <FaSearch className="movil-live-results__search-icon" />
              <input
                type="search"
                placeholder="Buscar por nombre o dorsal..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
              />
              {busqueda && (
                <button
                  type="button"
                  className="movil-live-results__search-clear"
                  onClick={() => setBusqueda('')}
                  aria-label="Limpiar búsqueda"
                >
                  ×
                </button>
              )}
            </div>

            <select
              className="movil-live-results__select"
              value={distancia}
              onChange={(e) => setDistancia(e.target.value)}
            >
              <option value="">Todas las distancias</option>
              {distancias.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
        )}

        {!cargando && disponibleActual && resultadosBase.length > 0 && (
          <div className="movil-live-results__tabs">
            <button
              type="button"
              className={`movil-live-results__tab ${
                viewMode === 'general' ? 'is-active' : ''
              }`}
              onClick={() => handleViewModeChange('general')}
            >
              General
            </button>
            <button
              type="button"
              className={`movil-live-results__tab ${
                viewMode === 'categorias' ? 'is-active' : ''
              }`}
              onClick={() => handleViewModeChange('categorias')}
            >
              Categorías
            </button>
          </div>
        )}

        {/* 🆕 Toggle de Género (solo visible en modo General) */}
        {!cargando &&
          disponibleActual &&
          viewMode === 'general' &&
          generosDisponibles.length > 0 && (
            <div className="movil-live-results__gender-tabs">
              {generosDisponibles.map((g) => (
                <button
                  key={g}
                  type="button"
                  className={`movil-live-results__gender-tab ${
                    genero === g ? 'is-active' : ''
                  }`}
                  onClick={() => setGenero(g)}
                >
                  {g}
                </button>
              ))}
            </div>
          )}

        {cargando ? (
          <div className="movil-live-results__state">
            <div className="movil-live-results__loader" />
            <p>Conectando con el cronometraje...</p>
          </div>
        ) : error ? (
          <div className="movil-live-results__state movil-live-results__state--error">
            <p>{error}</p>
          </div>
        ) : !disponibleActual || resultadosBase.length === 0 ? (
          <div className="movil-live-results__state">
            <FaClock className="movil-live-results__state-icon" />
            <h3>Esperando resultados</h3>
            <p>Los resultados aparecerán aquí cuando estén disponibles.</p>
          </div>
        ) : totalVisibles === 0 ? (
          <div className="movil-live-results__state">
            <h3>Sin resultados</h3>
            <p>Prueba cambiando los filtros.</p>
          </div>
        ) : (
          <div className="movil-live-results__groups">
            {grupos.map((grupo, gIdx) => (
              <section
                key={`${grupo.distancia}-${grupo.categoria}-${gIdx}`}
                className="movil-live-results__group"
              >
                <div className="movil-live-results__group-header">
                  <span className="movil-live-results__group-chip">
                    {grupo.distancia}
                  </span>
                  {viewMode === 'categorias' && (
                    <span className="movil-live-results__group-title">
                      {grupo.categoria}
                    </span>
                  )}
                  <span className="movil-live-results__group-count">
                    {grupo.resultados.length}
                  </span>
                </div>

                <div className="movil-live-results__rows">
                  {grupo.resultados.map((r, idx) => {
                    const tienePosicion =
                      r.posicion !== null && r.posicion !== undefined;
                    const noOk = esEstadoNoOk(r.estado);

                    return (
                      <div
                        key={`${r.dorsal}-${idx}`}
                        className={`movil-live-results__row ${
                          noOk ? 'is-no-ok' : ''
                        }`}
                      >
                        <span className="movil-live-results__pos">
                          {tienePosicion ? r.posicion : '—'}
                        </span>

                        <span className="movil-live-results__dorsal">
                          {r.dorsal || '—'}
                        </span>

                        <span className="movil-live-results__nombre">
                          {r.nombre || 'Sin nombre'}
                        </span>

                        {noOk ? (
                          <span
                            className={`movil-live-results__estado ${
                              String(r.estado).toUpperCase() === 'DNF'
                                ? 'is-dnf'
                                : String(r.estado).toUpperCase() === 'DNS'
                                ? 'is-dns'
                                : 'is-other'
                            }`}
                          >
                            {String(r.estado).toUpperCase()}
                          </span>
                        ) : (
                          <span className="movil-live-results__tiempo">
                            {r.tiempo || '—'}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}

        <footer className="movil-live-results__footer">
          <span>Resultados provisionales · Race Result</span>
          <span>InnovateSport · En vivo</span>
        </footer>
      </div>
    </div>
  );
};

export default LiveResultsMovil;